import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { UserPlus, CheckCircle2, Plus, Loader2, Zap, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import NewEnrollmentDialog from "./NewEnrollmentDialog";

export default function QuickActionSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [taskTxt, setTaskTxt] = useState("");
  const [doneTxt, setDoneTxt] = useState("");
  const [busy, setBusy] = useState("");
  const [enrollOpen, setEnrollOpen] = useState(false);

  const close = () => onOpenChange(false);

  const newLead = () => { close(); navigate("/leads?new=1"); };

  const quickEnroll = () => { close(); setEnrollOpen(true); };

  const addTask = async (tomorrow) => {
    const title = taskTxt.trim();
    if (!title) return;
    setBusy("task");
    try {
      await api.post("/tasks/quick", { title }, { params: tomorrow ? { day: "tomorrow" } : {} });
      setTaskTxt("");
      toast.success(tomorrow ? "Task added for tomorrow" : "Task added for today");
      close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const logDone = async () => {
    const title = doneTxt.trim();
    if (!title) return;
    setBusy("done");
    try {
      await api.post("/tasks/log-done", { title, touch_type: "note" });
      setDoneTxt("");
      toast.success("Done kaam log ho gaya ✅");
      close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl p-5 pb-8" data-testid="quick-action-sheet">
        <SheetTitle className="text-base font-heading mb-1">Quick actions</SheetTitle>
        <p className="text-xs text-muted-foreground mb-4">Ek tap mein kaam — lead banao, task add karo, ya done kaam log karo.</p>

        <Button onClick={newLead} className="w-full justify-start h-12 mb-3 bg-primary hover:bg-primary/90" data-testid="quick-new-lead">
          <UserPlus className="h-5 w-5 mr-2" /> New Lead
        </Button>

        <Button onClick={quickEnroll} variant="outline" className="w-full justify-start h-12 mb-5 border-primary/40 text-primary hover:bg-primary/5" data-testid="quick-enroll">
          <GraduationCap className="h-5 w-5 mr-2" /> Quick Enrollment
        </Button>

        <div className="space-y-2 mb-5">
          <div className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground"><Plus className="h-3.5 w-3.5 text-blue-600" /> Quick task</div>
          <div className="flex items-center gap-2">
            <Input value={taskTxt} onChange={(e) => setTaskTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTask(false); }} placeholder="Naya task likho…" className="h-11" data-testid="quick-task-input" />
            <Button className="h-11 shrink-0" onClick={() => addTask(false)} disabled={busy === "task" || !taskTxt.trim()} data-testid="quick-task-today">
              {busy === "task" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aaj"}
            </Button>
            <Button variant="outline" className="h-11 shrink-0" onClick={() => addTask(true)} disabled={busy === "task" || !taskTxt.trim()} data-testid="quick-task-tomorrow">Kal</Button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground"><Zap className="h-3.5 w-3.5 text-emerald-600" /> Log done work</div>
          <div className="flex items-center gap-2">
            <Input value={doneTxt} onChange={(e) => setDoneTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") logDone(); }} placeholder="Jo already kar diya…" className="h-11" data-testid="quick-done-input" />
            <Button className="h-11 shrink-0 bg-emerald-600 hover:bg-emerald-700" onClick={logDone} disabled={busy === "done" || !doneTxt.trim()} data-testid="quick-done-log">
              {busy === "done" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Log</>}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

      <NewEnrollmentDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        onEnrolled={() => { setEnrollOpen(false); toast.success("Enrollment ho gaya 🎉"); }}
      />
    </>
  );
}
